/**
 * Booth OS · uploadQueue.js
 *
 * Trust architecture for the kiosk: every upload is written to
 * IndexedDB **before** the network attempt, so the guest's
 * contribution is canonically preserved the moment they tap
 * "Preserve". The network leg is just delivery — local IDB is the
 * truth.
 *
 * Guests should never experience "failure" — only delayed
 * preservation. The booth always returns to the launcher; the QR
 * continuity always appears; the kiosk internally knows when a
 * memory still needs carrying onward.
 *
 * Retry triggers:
 *   • `online`         — browser fires when connectivity returns
 *   • `visibilitychange` → visible  — operator resumes the tablet
 *   • periodic heartbeat (30s, only while pending > 0)
 *
 * Backoff (per-item): 2s → 5s → 15s → 30s → 60s → 60s steady.
 *
 * Dedupe: every enqueue carries a stable `dedupe_id` (UUID). The
 * server's three booth upload endpoints check `(event_id, dedupe_id)`
 * before insert — a reconnect-retry never double-preserves.
 *
 * NO red banners. NO retry buttons. NO sync dashboards. The single
 * piece of editorial copy that ever leaks out is "Preserved locally
 * ✦", surfaced only when the kiosk knows network delivery hasn't
 * landed yet.
 */

const DB_NAME = 'eventdrop_booth_os';
const DB_VERSION = 1;
const STORE = 'uploads';

const BACKOFF_LADDER_MS = [2_000, 5_000, 15_000, 30_000, 60_000];
const HEARTBEAT_MS = 30_000;

// ── Stale-entry expiry policy ────────────────────────────────────
// The queue is invisible infrastructure. We deliberately don't
// surface retry counts, error banners, or anything resembling a
// dashboard — but we DO need an internal age-out so a permanently
// poisoned record can never fill IDB or thrash the kiosk for ever.
//
// Three orthogonal cut-offs, any one of which retires a record
// silently to the local archive (i.e. simply deleted from the
// queue without further attempts):
//
//   • MAX_QUEUE_AGE_MS   — 7 days. Beyond a week the event itself
//     is almost certainly past, the wall is closed, and replay can
//     never deliver. The memory was canonically preserved locally
//     in the kiosk's IDB at the moment of capture; we don't try
//     to push it forever.
//
//   • MAX_ATTEMPTS       — 50. At our 60s steady backoff that's
//     ~50 minutes of trying. If we haven't landed by then, the
//     destination is permanently unreachable (event deleted,
//     auth revoked, server config rejecting the kind).
//
//   • PERMANENT_STATUSES — 4xx responses that will never succeed
//     on retry. 401/403 (auth-permanent for the kiosk session),
//     404 (event archived), 409 (dedupe collision — server has
//     it), 410 (gone), 413 (oversized — replay won't help),
//     422 (validation — same payload will always reject).
//
// All three retire silently. No warning, no toast, no banner.
const MAX_QUEUE_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 50;
const PERMANENT_STATUSES = new Set([401, 403, 404, 409, 410, 413, 422]);

// ── BOOTH_QUEUE_TRACE — operator-only diagnostic flashlight ──────
//
// Activation (operator runs in DevTools, never any UI):
//     localStorage.setItem("BOOTH_QUEUE_TRACE", "1");
// Deactivation:
//     localStorage.removeItem("BOOTH_QUEUE_TRACE");
//
// Output policy:
//   • console.debug ONLY — never .warn / .error / alerts / overlays.
//   • Prefix every line with the literal `[BoothQueueTrace]` so the
//     operator can grep/filter during a live venue incident.
//
// Allowed events (close set — do NOT extend without philosophy review):
//   queued · replay-start · replay-success · replay-failure ·
//   aged-out · poisoned-retired · reconnect-drain · duplicate-skipped
//
// Allowed metadata: dedupe_id, endpoint (path only), age_ms,
//   retry_count, status code. NEVER guest names, messages, blobs,
//   event titles, auth tokens, QR codes, or pairing codes.
//
// Philosophy lock: this is a flashlight, not an operator console.
// It must remain dormant 99.9% of the time and lifesaving during
// the one catastrophic venue-WiFi incident. The moment a guest or
// host becomes aware of it, the tool has failed philosophically.
function traceEnabled() {
  try { return localStorage.getItem('BOOTH_QUEUE_TRACE') === '1'; }
  catch { return false; }
}

// Reduce an absolute URL to just the path so we never accidentally
// log a host's auth-bearing query string. Falls back to the raw
// value (the URL is already canonical in our codebase) if parsing
// trips on a relative path.
function safeEndpoint(url) {
  try { return new URL(url).pathname; } catch { return String(url || ''); }
}

function trace(event, rec, extra) {
  if (!traceEnabled()) return;
  const payload = {
    event,
    dedupe_id: rec?.dedupe_id,
    endpoint: rec?.url ? safeEndpoint(rec.url) : undefined,
    age_ms: rec?.queued_at ? Date.now() - rec.queued_at : undefined,
    retry_count: rec?.attempts,
    status: rec?.last_error_status || extra?.status,
    ...(extra || {}),
  };
  // Strip undefined keys for a tidy console line.
  for (const k of Object.keys(payload)) if (payload[k] === undefined) delete payload[k];
  // eslint-disable-next-line no-console
  console.debug('[BoothQueueTrace]', payload);
}

let dbPromise = null;
let heartbeatTimer = null;
let listenersRegistered = false;
const subscribers = new Set();
const inflight = new Set(); // dedupe_ids currently being sent

// ── IDB open ──────────────────────────────────────────────────────
function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'dedupe_id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('queued_at', 'queued_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; });
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

const putRecord  = (rec) => tx('readwrite', (s) => s.put(rec));
const getRecord  = (id)  => tx('readonly',  (s) => new Promise((res, rej) => { const r = s.get(id); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); }));
const deleteRecord = (id) => tx('readwrite', (s) => s.delete(id));
const allRecords = () => tx('readonly', (s) => new Promise((res, rej) => { const r = s.getAll(); r.onsuccess = () => res(r.result || []); r.onerror = () => rej(r.error); }));

// ── Subscribers ───────────────────────────────────────────────────
// Components subscribe to learn when the pending-count crosses
// zero (so "Preserved locally ✦" can flip back to "Your memory has
// been preserved ✦" without any banner / red dot).
export function subscribe(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}
async function notify() {
  const pending = await pendingCount();
  for (const fn of subscribers) try { fn({ pending }); } catch (_) {}
}
export async function pendingCount() {
  try {
    const all = await allRecords();
    return all.filter((r) => r.status !== 'sent').length;
  } catch (_) { return 0; }
}

// ── UUID (RFC4122-ish, sufficient for client-only dedupe) ────────
export function newDedupeId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// ── Serialise FormData → record-friendly arrays of {name, blob|str}
async function serializeFormData(fd) {
  const parts = [];
  // FormData has an entries() iterator in all modern browsers.
  // eslint-disable-next-line no-restricted-syntax
  for (const [name, value] of fd.entries()) {
    if (value instanceof Blob) {
      parts.push({ name, kind: 'blob', blob: value, filename: value.name || `field-${name}` });
    } else {
      parts.push({ name, kind: 'text', value: String(value) });
    }
  }
  return parts;
}
function rebuildFormData(parts) {
  const fd = new FormData();
  for (const p of parts) {
    if (p.kind === 'blob') fd.append(p.name, p.blob, p.filename);
    else fd.append(p.name, p.value);
  }
  return fd;
}

// ── Network send (single attempt) ─────────────────────────────────
async function attemptSend(rec) {
  const url = rec.url;
  let body, headers = {};
  if (rec.kind === 'multipart') {
    body = rebuildFormData(rec.parts);
    // fetch sets the multipart boundary automatically when given FormData.
  } else if (rec.kind === 'json') {
    body = JSON.stringify(rec.jsonBody);
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { method: 'POST', body, headers, credentials: 'include' });
  if (!res.ok) {
    const err = new Error(`upload ${res.status}`);
    err.status = res.status;
    throw err;
  }
  // Try to parse JSON — caller doesn't depend on response for
  // canonical preservation, but we surface it when we have it.
  try { return await res.json(); } catch { return null; }
}

// ── Drain pending queue ───────────────────────────────────────────
async function drain() {
  let all;
  try { all = await allRecords(); } catch (_) { return; }

  // First pass — silently retire records that have aged out or
  // hit a permanent failure mode. We delete them from IDB without
  // ever surfacing the retirement to the UI. The original memory
  // was preserved at capture time; the queue is just a delivery
  // attempt, and at some point delivery has to give up.
  const now = Date.now();
  for (const rec of all) {
    if (rec.status === 'sent') continue;
    const age = now - (rec.queued_at || 0);
    const aged    = age > MAX_QUEUE_AGE_MS;
    const maxed   = (rec.attempts || 0) >= MAX_ATTEMPTS;
    const poisoned = PERMANENT_STATUSES.has(rec.last_error_status || 0);
    if (aged || maxed || poisoned) {
      if (aged) trace('aged-out', rec);
      else trace('poisoned-retired', rec, { reason: maxed ? 'max-attempts' : 'permanent-status' });
      try { await deleteRecord(rec.dedupe_id); } catch (_) {}
    }
  }

  // Re-read after expiry so the drain operates on the surviving
  // set only.
  try { all = await allRecords(); } catch (_) { return; }

  // Identify records skipped this pass because they're already
  // mid-flight from a previous drain tick — these are "duplicates"
  // in the operational sense: the same dedupe_id is racing itself
  // because a heartbeat fired before the previous attempt resolved.
  for (const r of all) {
    if (r.status !== 'sent' && inflight.has(r.dedupe_id)) {
      trace('duplicate-skipped', r);
    }
  }

  const due = all
    .filter((r) => r.status !== 'sent')
    .filter((r) => !inflight.has(r.dedupe_id))
    .filter((r) => !r.next_attempt_at || Date.now() >= r.next_attempt_at)
    .sort((a, b) => (a.queued_at || 0) - (b.queued_at || 0));

  for (const rec of due) {
    inflight.add(rec.dedupe_id);
    trace('replay-start', rec);
    try {
      await attemptSend(rec);
      trace('replay-success', rec);
      try { await deleteRecord(rec.dedupe_id); } catch (_) {}
    } catch (e) {
      const status = e.status || 0;
      // Permanent failure → retire immediately. We don't wait for
      // the next heartbeat to clean it up; the queue should not
      // hold a record that we already know will never succeed.
      if (PERMANENT_STATUSES.has(status)) {
        trace('poisoned-retired', rec, { reason: 'permanent-status', status });
        try { await deleteRecord(rec.dedupe_id); } catch (_) {}
      } else {
        const attempts = (rec.attempts || 0) + 1;
        const delay = BACKOFF_LADDER_MS[Math.min(attempts - 1, BACKOFF_LADDER_MS.length - 1)];
        const next = { ...rec, attempts, last_error_status: status, next_attempt_at: Date.now() + delay };
        trace('replay-failure', next, { status });
        try { await putRecord(next); } catch (_) {}
      }
    } finally {
      inflight.delete(rec.dedupe_id);
    }
  }
  await notify();
  // While anything remains, keep the heartbeat alive. When the queue
  // empties, we let the heartbeat sleep until the next enqueue.
  const remaining = (await pendingCount());
  if (remaining > 0) ensureHeartbeat();
  else stopHeartbeat();
}

function ensureHeartbeat() {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => { drain(); }, HEARTBEAT_MS);
}
function stopHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function ensureGlobalListeners() {
  if (listenersRegistered) return;
  listenersRegistered = true;
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { trace('reconnect-drain', null, { trigger: 'online' }); drain(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        trace('reconnect-drain', null, { trigger: 'visibility' });
        drain();
      }
    });
  }
}

// ── Public API ────────────────────────────────────────────────────
/**
 * Mirror an upload to IDB, then try the network once. Returns:
 *   { ok: true, viaNetwork: boolean, response: any|null }
 *
 *  ok is ALWAYS true once written to IDB — that's canonical local
 *  preservation. `viaNetwork` tells the caller whether the network
 *  leg succeeded immediately, so the UI can pick its language:
 *   • true  → "Your memory has been preserved ✦"
 *   • false → "Preserved locally ✦"  (still calm, no banners)
 */
export async function sendOrQueue({ kind, url, formData, jsonBody, dedupeId }) {
  if (!dedupeId) dedupeId = newDedupeId();
  ensureGlobalListeners();

  const rec = {
    dedupe_id: dedupeId,
    status: 'pending',
    kind,
    url,
    parts: kind === 'multipart' && formData ? await serializeFormData(formData) : undefined,
    jsonBody: kind === 'json' ? jsonBody : undefined,
    queued_at: Date.now(),
    attempts: 0,
  };

  // 1) Canonical local preservation FIRST. If IDB write fails we
  //    fall through to the network anyway — better to ship than to
  //    block the guest journey on a quota issue.
  try { await putRecord(rec); } catch (_) {}
  trace('queued', rec);
  await notify();

  // 2) Network attempt. On success we delete the record (no need to
  //    hold the bytes once preserved server-side); on failure the
  //    record stays and the retry loop owns it.
  try {
    trace('replay-start', rec, { synchronous: true });
    const response = await attemptSend(rec);
    trace('replay-success', rec, { synchronous: true });
    try { await deleteRecord(dedupeId); } catch (_) {}
    await notify();
    return { ok: true, viaNetwork: true, response, dedupeId };
  } catch (e) {
    const status = e.status || 0;
    // Schedule the first backoff slot and start the heartbeat.
    try {
      await putRecord({ ...rec, attempts: 1, last_error_status: status, next_attempt_at: Date.now() + BACKOFF_LADDER_MS[0] });
    } catch (_) {}
    trace('replay-failure', { ...rec, attempts: 1, last_error_status: status }, { synchronous: true, status });
    ensureHeartbeat();
    return { ok: true, viaNetwork: false, response: null, dedupeId };
  }
}

/** Kick the drain manually — used by the kiosk shell on mount so
    any uploads queued in a previous session start flying again. */
export function kickDrain() {
  ensureGlobalListeners();
  trace('reconnect-drain', null, { trigger: 'kickDrain' });
  drain();
}
